/**
 * Physics Movement Manager
 * 
 * Main facade for physics-based movement.
 * Provides all movement functionality without requiring direct access to PhysicsMovement class.
 */

import { PhysicsMovement, type MovementConfig } from './Movement';
import type { Vector2 } from 'three';
import type { Object3D } from 'three';
import type { Body } from 'cannon-es';

export class PhysicsMovementManager {
    private movement: PhysicsMovement;

    /**
     * Create a Physics Movement Manager
     * @param config - Movement configuration (speed, sprint multiplier, jump force)
     * @param body - Cannon-es physics body
     * @param mesh - Three.js mesh (for rotation)
     */
    constructor(config: MovementConfig, body: Body, mesh: Object3D) {
        this.movement = new PhysicsMovement(config, body, mesh);
    }

    // ===== MOVEMENT METHODS =====

    /**
     * Apply movement with mesh rotation based on camera yaw
     * 
     * NOTE: deltaTime is not needed for physics movement as the physics engine
     * handles time-stepping internally. This method was updated to remove deltaTime.
     * 
     * @param inputVector - Input vector {x: strafe, y: forward}
     * @param cameraYaw - Camera yaw angle in radians
     * @param isSprinting - Whether sprint is active
     * @param rotationSmoothFactor - Rotation smoothing (default: 0.15)
     */
    applyMovementWithMeshRotation(
        inputVector: Vector2 | { x: number; y: number },
        cameraYaw: number,
        isSprinting: boolean = false,
        rotationSmoothFactor: number = 0.15
    ): void {
        this.movement.applyMovementWithMeshRotation(
            inputVector,
            cameraYaw,
            isSprinting,
            rotationSmoothFactor
        );
    }

    // ===== JUMPING =====

    /**
     * Make the character jump by applying upward impulse
     * Note: PhysicsMovement jumps are physics-based (impulse)
     * @param jumpPower - Optional custom jump force (uses config.jumpForce if not provided)
     * @returns True if jump was executed
     */
    jump(jumpPower?: number): boolean {
        return this.movement.jump(jumpPower);
    }

    // ===== CONFIGURATION =====

    /**
     * Set movement speed
     * @param speed - Movement force magnitude
     */
    setSpeed(speed: number): void {
        this.movement.setSpeed(speed);
    }

    /**
     * Get current movement speed
     * @returns Movement force magnitude
     */
    getSpeed(): number {
        return this.movement.getSpeed();
    }

    /**
     * Set sprint multiplier
     * @param multiplier - Sprint speed multiplier (default: 2.0)
     */
    setSprintMultiplier(multiplier: number): void {
        this.movement.setSprintMultiplier(multiplier);
    }

    /**
     * Get current sprint multiplier
     * @returns Sprint multiplier
     */
    getSprintMultiplier(): number {
        return this.movement.getSprintMultiplier();
    }

    // ===== UTILITY =====

    /**
     * Get the physics body being controlled
     * @returns Cannon-es Body or null if not set
     */
    getBody(): Body | null {
        return this.movement.getBody();
    }

    /**
     * Get the mesh being controlled
     * @returns Three.js Object3D or null if not set
     */
    getMesh(): Object3D | null {
        return this.movement.getMesh();
    }
}

